import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedReveal } from "@/components/MaskedReveal";
import { SignupCounter } from "@/components/SignupCounter";
import { scrollToId } from "@/lib/api";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_cowork-points/artifacts/tsz4uly0_image.png";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section id="top" ref={ref} className="relative overflow-hidden px-6 pb-24 pt-36 sm:px-10 sm:pt-44">
      <div className="mx-auto grid max-w-[1400px] items-end gap-10 lg:grid-cols-12">
        {/* Left — kinetic type */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-8 flex items-center gap-3"
          >
            <span className="h-px w-10 bg-terracotta" />
            <span className="font-body text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">
              Opening soon · Johor
            </span>
          </motion.div>

          <MaskedReveal
            as="h1"
            className="font-display font-light tracking-tighter text-espresso"
            lineClassName="text-6xl leading-[0.92] sm:text-7xl lg:text-[6.5rem]"
            lines={["A space for", "work, worth", "rising to."]}
            delay={0.35}
            animateOnLoad
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-xl font-body text-lg font-light leading-relaxed tracking-wide text-stone"
          >
            The first points-based, pay-as-you-use co-working concept — paired with
            specialty coffee by <span className="font-semibold text-espresso">The 18 Coffee Roasters</span>.
            Desks, meeting rooms and a lounge, measured by the moment, never the month.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap items-center gap-6"
          >
            <button
              data-testid="hero-waitlist-btn"
              onClick={() => scrollToId("waitlist")}
              className="group inline-flex items-center gap-3 rounded-full bg-espresso px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-[hsl(20_12%_25%)]"
            >
              Register your interest
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <span className="font-body text-sm font-light text-stone">
              Be first through the door.
            </span>
          </motion.div>

          <SignupCounter />
        </div>

        {/* Right — offset clipped portrait with parallax */}
        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative ml-auto aspect-[3/4] w-full max-w-[420px] overflow-hidden"
          >
            <motion.img
              src={HERO_IMG}
              alt="Scandinavian workspace interior"
              style={{ y, scale }}
              className="h-[112%] w-full object-cover"
              loading="eager"
            />
            <div className="pointer-events-none absolute inset-0 bg-espresso/10" />
            <div className="absolute bottom-5 left-5 font-body text-xs uppercase tracking-[0.2em] text-cream">
              Members' floor
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
